/**
 * WarmthSkillEffect — Fire tree Ember Fork: evolution replacing Ember.
 *
 * Replaces Ember's toggle with a dual-mode active:
 *   - **Ambient aura:** Always on after first activation. Functionally identical
 *     to Ember (2-tile base radius, 2 damage/s, 1% HP regen/enemy per 3s).
 *   - **Pulse mode:** On press, the aura expands to 3.5 tiles for 8 seconds with
 *     3 damage/s and doubled HP regen. Cooldown starts when the pulse expires.
 *
 * Inherits Hearthside bonuses through the 'ember' resolver scope:
 *   - `auraRadiusTiles` — added to ambient radius (not pulse radius).
 *   - `regenPerEnemy` — added to base heal percent (pulse doubles the result).
 *   - `residualBurnDurationMs` — Cinder's exit-burn applies to both modes.
 *
 * Pre-wires Hearth passive (ember_path_passive): when the player stands still
 * for `stationaryTriggerMs` and `regenMultiplier` is unlocked, heal ticks are
 * multiplied by the Hearth bonus. Applies to both ambient and pulse modes.
 *
 * Fizzle consumes cooldown. Breaks Hide on first activation.
 *
 * @see fire-spec.md § Warmth (`warmth`)
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Zone config
// ---------------------------------------------------------------------------

export interface WarmthZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly sourceSkillId: string;
  readonly followEntity: string;
  readonly onEntityExit?: (entityId: string) => void;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface WarmthDeps {
  /** Returns the player's current world position in pixels. */
  readonly getPlayerPosition: () => { x: number; y: number };
  /** Returns the player's entity ID (for zone follow-tracking). */
  readonly getPlayerEntityId: () => string;
  /** Returns entity IDs of enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];
  /** Deal direct damage to a target entity. */
  readonly dealDamage: (entityId: string, damage: number) => void;
  readonly player: {
    readonly maxHp: number;
    heal(amount: number): void;
  };
  /** Break stealth / hide state. */
  readonly breakHide: () => void;
  readonly zones: {
    create(config: WarmthZoneConfig): string;
    remove(zoneId: string): void;
    isInsideType(x: number, y: number, zoneType: string): boolean;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeId: string, key: string): number;
  };
  /**
   * Apply a status effect to a specific enemy entity.
   * Source-tagged per ADR-066. Used by Cinder's residual burn on zone exit.
   */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number; intensity: number; sourceSkillId: string },
  ) => void;
  /** Returns true if activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Returns how long the player has stood still (ms). Used by Hearth passive. */
  readonly getStationaryDurationMs?: () => number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface WarmthConfig {
  /** Cooldown between pulses in ms. Starts when the pulse expires. */
  readonly cooldownMs: number;
  // --- Ambient mode (Ember-equivalent) ---
  readonly ambientRadiusTiles: number;
  readonly ambientDamagePerTick: number;
  readonly ambientDamageTickMs: number;
  readonly ambientHealPercent: number;
  readonly ambientHealTickMs: number;
  // --- Pulse mode ---
  readonly pulseRadiusTiles: number;
  readonly pulseDurationMs: number;
  readonly pulseDamagePerTick: number;
  readonly pulseDamageTickMs: number;
  /** Multiplier applied to resolved heal percent during pulse. */
  readonly pulseRegenMultiplier: number;
}

const DEFAULT_CONFIG: WarmthConfig = {
  cooldownMs: 1500,              // medium cooldown tier
  // Ambient (Ember-equivalent)
  ambientRadiusTiles: 2,         // spec: same as Ember
  ambientDamagePerTick: 2,       // spec: 2 damage per tick
  ambientDamageTickMs: 1000,     // spec: every 1000ms
  ambientHealPercent: 0.01,      // spec: 1% max HP per enemy
  ambientHealTickMs: 3000,       // spec: every 3 seconds
  // Pulse
  pulseRadiusTiles: 3.5,         // spec: 3.5 tiles
  pulseDurationMs: 8000,         // spec: 8 seconds
  pulseDamagePerTick: 3,         // spec: 3 damage per second
  pulseDamageTickMs: 1000,       // spec: same interval
  pulseRegenMultiplier: 2.0,     // spec: doubled HP regen
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class WarmthSkillEffect implements SkillEffect {
  readonly skillId = 'warmth';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly _deps: WarmthDeps;
  private readonly _config: WarmthConfig;

  // State
  private _ambientActive = false;
  private _isPulsing = false;
  private _pulseRemainingMs = 0;
  private _zoneId: string | null = null;
  private _cooldownRemaining = 0;

  // Tick accumulators
  private _damageAccumulator = 0;
  private _healAccumulator = 0;

  // Stats (for scenario assertions)
  private _activations = 0;
  private _fizzles = 0;
  private _totalDamageDealt = 0;
  private _totalHealAmount = 0;
  private _pulseCount = 0;

  constructor(deps: WarmthDeps, config: Partial<WarmthConfig> = {}) {
    this._deps = deps;
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get totalHealAmount(): number { return this._totalHealAmount; }
  get pulseCount(): number { return this._pulseCount; }
  get isPulsing(): boolean { return this._isPulsing; }
  get zoneId(): string | null { return this._zoneId; }

  // --- SkillEffect interface ---

  isActive(): boolean { return this._ambientActive; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle.
    if (!this._deps.rollSkillSuccess('warmth')) {
      this._fizzles++;
      this._cooldownRemaining = this._config.cooldownMs;
      return false;
    }

    // First activation: establish the ambient aura.
    if (!this._ambientActive) {
      this._deps.breakHide();
      this._ambientActive = true;
    }

    // Start (or restart) the pulse.
    this._startPulse();
    this._activations++;
    return true;
  }

  deactivate(): void {
    if (!this._ambientActive) return;
    this._ambientActive = false;
    this._isPulsing = false;
    this._pulseRemainingMs = 0;
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    this._cooldownRemaining = this._config.cooldownMs;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (!this._ambientActive) return;

    // Pulse countdown.
    if (this._isPulsing) {
      this._pulseRemainingMs -= delta;
      if (this._pulseRemainingMs <= 0) {
        this._endPulse();
      }
    }

    // Tick damage and healing.
    this._damageAccumulator += delta;
    this._healAccumulator += delta;

    const damageTickMs = this._isPulsing
      ? this._config.pulseDamageTickMs
      : this._config.ambientDamageTickMs;

    while (this._damageAccumulator >= damageTickMs) {
      this._damageAccumulator -= damageTickMs;
      this._applyDamageTick();
    }

    while (this._healAccumulator >= this._config.ambientHealTickMs) {
      this._healAccumulator -= this._config.ambientHealTickMs;
      this._applyHealTick();
    }
  }

  destroy(): void {
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    this._ambientActive = false;
    this._isPulsing = false;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }

  // ---------------------------------------------------------------------------
  // Internal — zone management
  // ---------------------------------------------------------------------------

  private _startPulse(): void {
    // Remove existing zone (ambient or previous pulse).
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
    }

    const pos = this._deps.getPlayerPosition();
    const entityId = this._deps.getPlayerEntityId();

    this._zoneId = this._deps.zones.create({
      id: `warmth-pulse-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'warmth_pulse',
      position: pos,
      shape: 'circle',
      radiusTiles: this._config.pulseRadiusTiles,
      sourceSkillId: 'warmth',
      followEntity: entityId,
      onEntityExit: this._makeExitCallback(),
    });

    this._isPulsing = true;
    this._pulseRemainingMs = this._config.pulseDurationMs;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
    this._pulseCount++;
  }

  private _endPulse(): void {
    this._isPulsing = false;
    this._pulseRemainingMs = 0;
    this._cooldownRemaining = this._config.cooldownMs;

    // Swap pulse zone for ambient zone.
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
    }

    this._createAmbientZone();
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }

  private _createAmbientZone(): void {
    const pos = this._deps.getPlayerPosition();
    const entityId = this._deps.getPlayerEntityId();

    // Hearthside radius bonus (inherited from Ember scope).
    const bonusRadius = this._deps.resolver.getAdditiveEffect('ember', 'auraRadiusTiles');
    const resolvedRadius = this._config.ambientRadiusTiles + bonusRadius;

    this._zoneId = this._deps.zones.create({
      id: `warmth-ambient-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'ember_aura',
      position: pos,
      shape: 'circle',
      radiusTiles: resolvedRadius,
      sourceSkillId: 'warmth',
      followEntity: entityId,
      onEntityExit: this._makeExitCallback(),
    });
  }

  /**
   * Builds the Cinder exit callback when `residualBurnDurationMs` is unlocked.
   * Returns undefined when Cinder is not active.
   */
  private _makeExitCallback(): ((exitEntityId: string) => void) | undefined {
    const residualBurnMs = this._deps.resolver.getAdditiveEffect(
      'ember',
      'residualBurnDurationMs',
    );
    if (residualBurnMs <= 0) return undefined;
    return (exitEntityId: string) => {
      this._deps.applyEnemyStatus(exitEntityId, 'burning', {
        durationMs: residualBurnMs,
        intensity: this._config.ambientDamagePerTick,
        sourceSkillId: 'ember_linger',
      });
    };
  }

  // ---------------------------------------------------------------------------
  // Internal — tick logic
  // ---------------------------------------------------------------------------

  private _getCurrentRadius(): number {
    if (this._isPulsing) {
      return this._config.pulseRadiusTiles;
    }
    const bonusRadius = this._deps.resolver.getAdditiveEffect('ember', 'auraRadiusTiles');
    return this._config.ambientRadiusTiles + bonusRadius;
  }

  private _applyDamageTick(): void {
    const radius = this._getCurrentRadius();
    const pos = this._deps.getPlayerPosition();
    const enemies = this._deps.getEnemiesInRadius(pos.x, pos.y, radius);
    const damage = this._isPulsing
      ? this._config.pulseDamagePerTick
      : this._config.ambientDamagePerTick;

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, damage);
      this._totalDamageDealt += damage;
    }
  }

  private _applyHealTick(): void {
    const radius = this._getCurrentRadius();
    const pos = this._deps.getPlayerPosition();
    const enemies = this._deps.getEnemiesInRadius(pos.x, pos.y, radius);
    const enemyCount = enemies.length;

    if (enemyCount === 0) return;

    // Base heal percent + Hearthside bonus (inherited from Ember scope).
    const bonusHeal = this._deps.resolver.getAdditiveEffect('ember', 'regenPerEnemy');
    let resolvedHealPercent = this._config.ambientHealPercent + bonusHeal;

    // Pulse doubles the heal.
    if (this._isPulsing) {
      resolvedHealPercent *= this._config.pulseRegenMultiplier;
    }

    // Deep Hearth: stacked regen bonus when inside a bonfire zone.
    const stackedBonus = this._deps.resolver.getAdditiveEffect('bonfire', 'stackedRegenBonus');
    if (stackedBonus > 0 && this._deps.zones.isInsideType(pos.x, pos.y, 'bonfire_zone')) {
      resolvedHealPercent *= (1 + stackedBonus);
    }

    // Hearth passive: stationary regen multiplier.
    const triggerMs = this._deps.resolver.getTreePassiveEffect('fire', 'stationaryTriggerMs');
    if (triggerMs > 0) {
      const stationaryMs = this._deps.getStationaryDurationMs?.() ?? 0;
      if (stationaryMs >= triggerMs) {
        const regenMultiplier = this._deps.resolver.getTreePassiveEffect(
          'fire',
          'regenMultiplier',
        );
        if (regenMultiplier > 0) {
          resolvedHealPercent *= regenMultiplier;
        }
      }
    }

    const healAmount = Math.round(
      this._deps.player.maxHp * resolvedHealPercent * enemyCount,
    );
    if (healAmount > 0) {
      this._deps.player.heal(healAmount);
      this._totalHealAmount += healAmount;
    }
  }
}
